import { Link } from "react-router-dom";
import { Eye, Clock, Play, Volume2, Lock } from "lucide-react";

function MediaBadge({ type, premium }) {
  if (premium)
    return (
      <span className="absolute top-3 left-3 bg-[color:var(--accent)] text-white kicker px-2 py-1 flex items-center gap-1" data-testid="badge-premium">
        <Lock size={12} /> Subscribers
      </span>
    );
  if (type === "video")
    return (
      <span className="absolute top-3 left-3 bg-[color:var(--accent)] text-white kicker px-2 py-1 flex items-center gap-1" data-testid="badge-video">
        <Play size={12} /> Video
      </span>
    );
  if (type === "audio")
    return (
      <span className="absolute top-3 left-3 bg-black text-white kicker px-2 py-1 flex items-center gap-1" data-testid="badge-audio">
        <Volume2 size={12} /> Audio
      </span>
    );
  return null;
}

export default function ArticleCard({ article, size = "md" }) {
  const isLg = size === "lg";
  const isSm = size === "sm";

  if (isSm) {
    return (
      <Link to={`/article/${article.slug}`} className="group flex gap-4 border-b py-4" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`article-card-${article.slug}`}>
        <div className="relative w-24 h-24 md:w-28 md:h-28 shrink-0 overflow-hidden img-zoom">
          <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
          {article.premium && (
            <span className="absolute top-1 left-1 bg-[color:var(--accent)] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 flex items-center gap-1" data-testid="badge-premium">
              <Lock size={9} />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="kicker text-[color:var(--accent)] mb-1 flex items-center gap-2">
            {article.category_name}
            {article.premium && <span className="text-[9px] font-bold">· SUBSCRIBERS</span>}
          </div>
          <h3 className="font-serif text-lg md:text-xl leading-tight font-bold group-hover:text-[color:var(--accent)] transition-colors line-clamp-3">
            {article.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-[color:var(--text-muted)]">
            <span>{new Date(article.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
            <span className="flex items-center gap-1"><Clock size={10} /> {article.read_time} min</span>
            {typeof article.views === "number" && article.views > 0 && (
              <span className="flex items-center gap-1" data-testid={`views-${article.slug}`}><Eye size={10} /> {article.views.toLocaleString()}</span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.slug}`} className="group block" data-testid={`article-card-${article.slug}`}>
      <div className={`relative overflow-hidden img-zoom ${isLg ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
        <MediaBadge type={article.media_type} premium={article.premium} />
      </div>
      <div className="pt-4">
        <div className="kicker text-[color:var(--accent)] mb-2">{article.category_name}</div>
        <h3 className={`font-serif font-bold leading-[1.05] tracking-tight group-hover:text-[color:var(--accent)] transition-colors ${isLg ? "text-3xl md:text-5xl" : "text-xl md:text-2xl"}`}>
          {article.title}
        </h3>
        {isLg && (
          <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed line-clamp-3 max-w-xl">{article.excerpt}</p>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs text-[color:var(--text-muted)]">
          <span>By <span className="text-[color:var(--text)] font-semibold">{article.author_name}</span></span>
          <span>·</span>
          <span>{new Date(article.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {article.read_time} min</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Eye size={12} /> {article.views}</span>
        </div>
      </div>
    </Link>
  );
}
